<template>
  <div class="flex flex-col gap-0">
    <h2 class="m-0 mb-1.5 text-2xl font-bold text-vault-text-primary">{{ $t('walletWithdrawal.title') }}</h2>
    <p class="m-0 mb-4.5 text-vault-text-secondary">{{ $t('walletWithdrawal.subtitle') }}</p>

    <input
      v-model="destinationInput"
      type="text"
      class="input-base font-mono"
      placeholder="0x..."
      :disabled="loading"
    />
    <div v-if="hasError" class="mt-2 text-vault-danger-400 text-[13px]">
      {{ $t('walletWithdrawal.invalidAddress') }}
    </div>

    <div class="my-6 p-4.5 rounded-2xl bg-blue-50/70 border border-vault-blue-400/10">
      <div class="flex justify-between items-center mb-2.5 text-sm">
        <span>{{ $t('walletWithdrawal.balance') }}</span>
        <strong class="text-vault-text-primary">{{ balance }} {{ currency || 'ETH' }}</strong>
      </div>
      <div v-if="isGasless" class="flex justify-between items-center mb-2.5 text-sm">
        <span>{{ $t('walletWithdrawal.protocolFee') }}</span>
        <span class="text-vault-text-secondary">{{ protocolFee }} {{ currency || 'ETH' }}</span>
      </div>
      <div class="flex justify-between items-center mb-2.5 text-sm">
        <span>{{ $t('walletWithdrawal.gasFee') }}</span>
        <span v-if="isGasless" class="text-vault-success-500 font-semibold">{{ $t('walletWithdrawal.paidByPaymaster') }}</span>
        <span v-else class="text-vault-text-secondary">{{ estimatedGasFee }} {{ currency || 'ETH' }}</span>
      </div>
      <div class="flex justify-between items-center text-base font-semibold pt-2.5 border-t border-vault-blue-400/10 mt-2.5 mb-0">
        <span>{{ $t('walletWithdrawal.youWillReceive') }}</span>
        <strong class="text-vault-text-primary">{{ netAmount }} {{ currency || 'ETH' }}</strong>
      </div>
    </div>

    <div class="flex justify-between gap-3 sm:flex-col">
      <button type="button" class="btn-ghost" :disabled="loading" @click="handleCancel">
        {{ $t('common.back') }}
      </button>
      <button
        type="button"
        class="btn-primary flex-1"
        :disabled="loading || !isValid"
        @click="handleSubmit"
      >
        {{ loading ? $t('walletWithdrawal.submitting') : transferButtonText }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { validateAddress } from '@tokenbox/wallet-kit';

const { t: $t } = useI18n();

const props = withDefaults(defineProps<{
  balance: string;
  currency?: string;
  isGasless?: boolean;
  loading?: boolean;
}>(), {
  currency: 'ETH',
  isGasless: false,
  loading: false
});

const emit = defineEmits<{
  submit: [{ destination: string }];
  cancel: [];
}>();

const destinationInput = ref('');

const isValid = computed(() => {
  const value = destinationInput.value.trim();
  if (!value) return false;
  return validateAddress(value);
});

const hasError = computed(() => {
  return Boolean(destinationInput.value) && !isValid.value;
});

const protocolFee = computed(() => {
  if (!props.balance) return '0.0000';
  const balance = Number.parseFloat(props.balance);
  const fee = balance * 0.0025; // 0.25%
  return fee.toFixed(4);
});

const estimatedGasFee = computed(() => {
  // Estimated gas fee for native ETH transfers
  return '0.0006';
});

const netAmount = computed(() => {
  if (!props.balance) return '0.0000';
  const balance = Number.parseFloat(props.balance);

  if (props.isGasless) {
    // Gasless: only protocol fee
    const fee = balance * 0.0025;
    const net = balance - fee;
    return net.toFixed(4);
  } else {
    // Regular: balance minus gas
    const gasEstimate = Number.parseFloat(estimatedGasFee.value);
    const receive = Math.max(0, balance - gasEstimate);
    return receive.toFixed(4);
  }
});

const transferButtonText = computed(() => {
  if (props.isGasless) {
    return $t('walletWithdrawal.transferGasless');
  }
  return $t('walletWithdrawal.transferAll');
});

function handleSubmit() {
  if (!isValid.value || props.loading) return;

  const destination = destinationInput.value.trim();
  emit('submit', { destination });
}

function handleCancel() {
  if (props.loading) return;
  emit('cancel');
}
</script>

<style scoped>
/* No custom styles needed - using Tailwind + index.css utilities */
</style>
